/**
 * Navigation service for handling question navigation
 */

/**
 * Handles navigation to the previous question
 * @param {Object} params - Navigation parameters
 * @param {number} params.currentQuestionIndex - The current question index
 * @param {Function} params.setCurrentQuestionIndex - Function to set the current question index
 * @param {Array} params.questionHistory - The question history array
 * @param {Function} params.resetQuestionState - Function to reset the question state
 * @returns {void}
 */
export const navigateToPreviousQuestion = (params) => {
  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    questionHistory,
    resetQuestionState
  } = params;
  
  if (currentQuestionIndex > 0) {
    resetQuestionState();
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  } else if (questionHistory.length > 0) {
    // If we're at the first question but have history
    resetQuestionState();
    setCurrentQuestionIndex(questionHistory[questionHistory.length - 1]);
  }
};

/**
 * Handles navigation to the next question
 * @param {Object} params - Navigation parameters
 * @param {number} params.currentQuestionIndex - The current question index
 * @param {Function} params.setCurrentQuestionIndex - Function to set the current question index
 * @param {Array} params.questions - The questions array
 * @param {Array} params.mockQuestions - The mock questions array
 * @param {Function} params.addToQuestionHistory - Function to add the current question to history
 * @param {Function} params.resetQuestionState - Function to reset the question state
 * @returns {void}
 */
export const navigateToNextQuestion = (params) => {
  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    questions,
    mockQuestions,
    addToQuestionHistory,
    resetQuestionState
  } = params;
  
  const questionsArray = questions && questions.length > 0 ? questions : mockQuestions;
  
  // console.log('[navigateToNextQuestion] ➡️ Navigation requested:', {
  //   currentIndex: currentQuestionIndex,
  //   questionsLength: questionsArray.length,
  //   canNavigate: currentQuestionIndex < questionsArray.length - 1,
  //   timestamp: new Date().toISOString()
  // });
  
  if (currentQuestionIndex < questionsArray.length - 1) {
    addToQuestionHistory(currentQuestionIndex);
    resetQuestionState();
    const newIndex = currentQuestionIndex + 1;
    // console.log('[navigateToNextQuestion] ✅ Setting currentQuestionIndex to:', newIndex);
    setCurrentQuestionIndex(newIndex);
  } else {
    // console.log('[navigateToNextQuestion] ⚠️ Already at last question, cannot navigate');
  }
};

/**
 * Handles navigation to a specific question by index
 * @param {Object} params - Navigation parameters
 * @param {number} params.targetIndex - The target question index
 * @param {number} params.currentQuestionIndex - The current question index
 * @param {Function} params.setCurrentQuestionIndex - Function to set the current question index
 * @param {Function} params.addToQuestionHistory - Function to add the current question to history
 * @param {Function} params.resetQuestionState - Function to reset the question state
 * @returns {void}
 */
export const navigateToQuestionByIndex = (params) => {
  const {
    targetIndex,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    addToQuestionHistory,
    resetQuestionState
  } = params;
  
  // console.log('[navigateToQuestionByIndex] 🎯 Navigation requested:', {
  //   from: currentQuestionIndex,
  //   to: targetIndex,
  //   timestamp: new Date().toISOString()
  // });
  
  if (targetIndex !== currentQuestionIndex) {
    addToQuestionHistory(currentQuestionIndex);
    resetQuestionState();
    // console.log('[navigateToQuestionByIndex] ✅ Setting currentQuestionIndex to:', targetIndex);
    // console.log('[navigateToQuestionByIndex] ✅ This corresponds to question number:', targetIndex + 1);
    setCurrentQuestionIndex(targetIndex);
  } else {
    // console.log('[navigateToQuestionByIndex] ⚠️ Target index same as current, no change needed');
  }
};

/**
 * Handles navigation to the next question in a list
 * @param {Object} params - Navigation parameters
 * @param {number} params.currentQuestionIndex - The current question index
 * @param {Function} params.setCurrentQuestionIndex - Function to set the current question index
 * @param {Object} params.loadedList - The loaded list object
 * @param {Function} params.addToQuestionHistory - Function to add the current question to history
 * @param {Function} params.resetQuestionState - Function to reset the question state
 * @param {Function} params.setLoadedListProgress - Function to set the loaded list progress
 * @returns {boolean} True if navigation was successful, false otherwise
 */
export const navigateToNextQuestionInList = (params) => {
  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    loadedList,
    addToQuestionHistory,
    resetQuestionState,
    setLoadedListProgress
  } = params;
  
  if (!loadedList || !loadedList.questions || loadedList.questions.length === 0) {
    return false;
  }
  
  // Find the current question's position in the list
  const currentPosition = loadedList.questions.findIndex(q => q === currentQuestionIndex);
  
  // If found and not at the end of the list
  if (currentPosition !== -1 && currentPosition < loadedList.questions.length - 1) {
    // Navigate to the next question in the list
    addToQuestionHistory(currentQuestionIndex);
    resetQuestionState();
    setCurrentQuestionIndex(loadedList.questions[currentPosition + 1]);
    setLoadedListProgress(currentPosition + 2); // +2 because we're 1-indexing for display
    return true;
  }
  
  return false;
};

/**
 * Updates question navigation progress
 * @param {Object} params - Navigation parameters
 * @param {number} params.currentQuestionIndex - The current question index
 * @param {number} params.totalQuestions - The total number of questions
 * @param {Function} params.setCurrentQuestionNumber - Function to set the current question number
 * @param {Function} params.setProgressPercentage - Function to set the progress percentage
 * @returns {void}
 */
export const updateQuestionProgress = (params) => {
  const {
    currentQuestionIndex,
    totalQuestions,
    setCurrentQuestionNumber,
    setProgressPercentage
  } = params;
  
  const currentNumber = currentQuestionIndex + 1;
  setCurrentQuestionNumber(currentNumber);
  
  if (totalQuestions > 0) {
    const percentage = Math.floor((currentNumber / totalQuestions) * 100);
    setProgressPercentage(percentage);
  } else {
    setProgressPercentage(0);
  }
};
